import { Observable } from 'rxjs';

import { ICredentials } from '@topcon/twc-angular';

import { CommonsConfigEnvironment } from '@mirage-projects/cw-models';

export interface GetDiagnosticsFileParams {
    durationDays: number;
}
/**
 * Service to interact with different aspects of the Magnet Platform.
 */
export abstract class MagnetService {
    static SITE_NAME = 'cr';
    static COLLAGE_NAME = 'collage';
    static VERITY_NAME = 'verity';

    readonly CLOUD_CONNECTOR_URL = 'https://collage.magnet-enterprise.com/downloads/CollageCloudConnector_v1.2.2.zip';

    constructor(
        protected configEnvironment: CommonsConfigEnvironment,
        protected location: Location
    ) {}

    isSite(): boolean {
        return !!this.configEnvironment?.isSite;
    }

    isConnectingFromMagnet(): boolean {
        return !this.isSite();
    }

    getVerityHost(): string {
        return this.configEnvironment.verityHost;
    }

    getVerityRedirection(): string {
        return this.configEnvironment.verityRedirection;
    }

    isVerity(): boolean {
        return this.location.host === this.getVerityHost();
    }

    isEmbed(): boolean {
        return window.location.pathname.includes('/embed.html');
    }

    getEnvName(): string {
        return this.configEnvironment.name;
    }

    getAppName(): string {
        let appName = MagnetService.SITE_NAME;
        if (!this.isSite()) {
            if (this.isVerity()) {
                appName = MagnetService.VERITY_NAME;
            } else {
                appName = MagnetService.COLLAGE_NAME;
            }
        }
        return appName;
    }

    /**
     * Returns the URL to Collage login page.
     */
    getCollageLoginUrl(): string {
        const collageLoginUrl = this.configEnvironment.API_CONFIG_CORE.collageLogin;
        if (this.isSite()) {
            return collageLoginUrl;
        }
        // Verity currently is deprecated maybe we will need to review this parameters if gets enabled again
        const verityState = this.isVerity() ? { state: 'app=verity' } : {};
        const collageWebLoginUrl = collageLoginUrl ? collageLoginUrl : this.generateCollageWebLoginUrl(verityState);
        return collageWebLoginUrl;
    }

    getEpsgServer(): string {
        return this.configEnvironment.epsgServer;
    }

    /**
     * Get the diagnostics file data to be sent to the support user
     * @param projectId Id of the project that need support
     * @param params Object with a durationDays property with the days that the support inmpersonation will be allowed
     */
    abstract getDiagnosticsFile(projectId: number, data: GetDiagnosticsFileParams): Observable<Record<string, unknown>>;

    private generateCollageWebLoginUrl(config: Partial<ICredentials> = {}): string {
        if (!this.configEnvironment.topconOne) {
            throw new Error('Collage Web needs the topconOne configuration.');
        }

        const { auth_host, scope, client_id, state, redirect_uri } = { ...this.configEnvironment.topconOne, ...config };
        const path = '/as/authorization.oauth2';
        let authUrl = `${auth_host}${path}?response_type=code`;
        authUrl += `&scope=${scope}`;
        authUrl += `&client_id=${client_id}`;
        authUrl += `&state=${encodeURIComponent(state || `${location.pathname}`)}`;
        if (redirect_uri) {
            authUrl += `&redirect_uri=${encodeURIComponent(redirect_uri)}`;
        }

        return authUrl;
    }
}
